import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import DialogTitle from '@mui/material/DialogTitle';
import { Button, Paper, Stack, Typography } from '@mui/material';
import { useTranslation } from '../../contexts/TranslationContext';
import {
    MutableRefObject,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import useApi from '../../api';
import TextEditorFormField from '../form/fields/texteditor';
import SelectionFormField from '../form/fields/selection';
import { FormChoices } from '../../api/types/form';
import { camelCaseObject } from '../../utils/api';
import { LoadingOutlined } from '@ant-design/icons';

type TranslateObjState = {
    text?: string;
    translatedText?: string;
    sourceLocale?: string;
    targetLocale?: string;
};

export type TranslateDialogRef = {
    open: () => void;
    close: () => void;
    setTranslateObj: (translateObj: TranslateObjState) => void;
    registerUpdateCallback: (
        callback: (
            translatedText: string | null | undefined,
            targetLocale: string,
        ) => void,
    ) => void;
};

type TranslateDialogProps = {
    dialogRef: MutableRefObject<TranslateDialogRef | undefined>;
};

type DeeplLangArrayType = Array<{ name: string; code: string }>;

export default function TranslateDialog({ dialogRef }: TranslateDialogProps) {
    const { translate } = useTranslation();
    const { cms } = useApi();

    const saveCallbackRef = useRef<Function>();
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    const [supportedLanguages, setSupportedLanguages] =
        useState<DeeplLangArrayType>([]);
    const [translateObj, setTranslateObj] = useState<TranslateObjState>({
        text: '',
        translatedText: undefined,
        sourceLocale: undefined,
        targetLocale: 'de',
    });

    const loadLanguages = () => {
        cms.getSupportedLanguages()
            .then(response => {
                if (response && !response.error) {
                    setSupportedLanguages(response.languages);
                }
            })
            .catch(console.log);
    };

    useEffect(() => {
        if (dialogRef) {
            dialogRef.current = {
                open: () => {
                    loadLanguages();
                    setOpen(true);
                },
                close: () => setOpen(false),
                setTranslateObj: newValues =>
                    setTranslateObj({ ...translateObj, ...newValues }),
                registerUpdateCallback: (callback: Function) =>
                    (saveCallbackRef.current = callback),
            };
        }
    }, [dialogRef]);

    const handleClose = () => {
        setOpen(false);
    };

    const translateNow = () => {
        if (
            typeof translateObj.text === 'string' &&
            typeof translateObj.targetLocale === 'string'
        ) {
            setLoading(true);
            cms.translateAnyText({
                text: translateObj.text ?? '',
                targetLocale: translateObj.targetLocale.toLowerCase() ?? 'de',
            })
                .then(response => {
                    setLoading(false);
                    if (response && !response.error) {
                        setTranslateObj(camelCaseObject(response));
                    }
                })
                .catch(() => setLoading(false));
        }
    };

    const saveTranslatedText = () => {
        if (saveCallbackRef.current) {
            saveCallbackRef.current(translateObj.translatedText);
        }
        handleClose();
    };

    const buildChoices = useCallback((): FormChoices => {
        const response: FormChoices = {};
        for (const lang of supportedLanguages) {
            response[lang.name] = lang.code;
        }
        return response;
    }, [supportedLanguages]);

    return useMemo(() => {
        return (
            <Dialog open={open} maxWidth="sm" fullWidth onClose={handleClose}>
                <DialogTitle variant="h5">Wert übersetzen</DialogTitle>
                <DialogContent>
                    <Stack spacing={2}>
                        <Paper
                            sx={{
                                p: 2,
                                background: theme =>
                                    theme.palette.background.paper,
                            }}>
                            <SelectionFormField
                                type="choice"
                                name="targetLocale"
                                label="form.translate.target_locale"
                                required
                                value={translateObj.targetLocale}
                                onChange={newVal =>
                                    setTranslateObj({
                                        ...translateObj,
                                        targetLocale: newVal,
                                    })
                                }
                                choices={buildChoices()}
                            />
                        </Paper>
                        <Stack spacing={1}>
                            <Typography color="text.secondary">
                                {translate('form.translate.input_title')}
                            </Typography>
                            <Paper
                                sx={{
                                    p: 2,
                                    background: theme =>
                                        theme.palette.background.paper,
                                }}>
                                <TextEditorFormField
                                    required
                                    type="texteditor"
                                    name="translateInputText"
                                    label="form.translate.input"
                                    value={translateObj.text}
                                    onChange={newVal =>
                                        setTranslateObj({
                                            ...translateObj,
                                            text: newVal,
                                        })
                                    }
                                />
                            </Paper>
                        </Stack>
                        <Stack
                            alignItems="center"
                            justifyContent="center"
                            minHeight={loading ? 75 : 0}>
                            {loading ? (
                                <LoadingOutlined style={{ fontSize: 24 }} />
                            ) : translateObj.translatedText ? (
                                <Paper
                                    sx={{
                                        p: 2,
                                        background: theme =>
                                            theme.palette.background.paper,
                                        width: '100%',
                                    }}>
                                    <TextEditorFormField
                                        required
                                        type="texteditor"
                                        name="translateOutputText"
                                        label="form.translate.output"
                                        value={translateObj.translatedText}
                                        onChange={console.log}
                                    />
                                </Paper>
                            ) : null}
                        </Stack>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>
                        {translate('form.general.cancel')}
                    </Button>
                    <Button
                        onClick={translateNow}
                        variant="text"
                        color="info"
                        disabled={!Boolean(translateObj.targetLocale)}>
                        {translate('form.general.field_context.translate_val')}
                    </Button>
                    {translateObj.translatedText && (
                        <Button
                            onClick={saveTranslatedText}
                            variant="contained">
                            {translate('form.general.save')}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        );
    }, [open, loading, translateObj, buildChoices, translate]);
}
